import { useId, useState } from "react";
import { type AdminProviderAccount, type AdminRejectReason, type ProviderApprovalStatus } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";

export const REJECT_REASONS: Array<{ value: AdminRejectReason; label: string }> = [
  { value: "policy_violation", label: "Policy violation" },
  { value: "duplicate_account", label: "Duplicate account" },
  { value: "incomplete_data", label: "Incomplete information" },
  { value: "fraud_risk", label: "Fraud risk" },
  { value: "other", label: "Other" },
];

export function canResendProviderOwnerSetup(role: string | undefined) {
  return role === "admin";
}

/** Which decisions a staff member may take on a provider in its current status. */
export function providerDecisions(status: ProviderApprovalStatus, isAdmin: boolean): ProviderApprovalStatus[] {
  const options: Record<ProviderApprovalStatus, ProviderApprovalStatus[]> = {
    pending: ["approved", "rejected"],
    approved: ["suspended"],
    suspended: ["approved"],
    rejected: ["approved"],
  };
  // Rejecting or suspending signs every member out, so only admins can.
  return options[status].filter((next) => isAdmin || next === "approved");
}

export interface ProviderDecisionInput {
  status: ProviderApprovalStatus;
  note: string;
  reason: AdminRejectReason | "";
}

/**
 * Decision controls for one provider. The note survives a failed request,
 * because the parent keeps it (see ``notes``/``onNoteChange``).
 */
export function ProviderDecision({
  busy,
  isAdmin,
  note,
  onDecide,
  onNoteChange,
  provider,
}: {
  busy: boolean;
  isAdmin: boolean;
  note: string;
  onDecide: (input: ProviderDecisionInput) => void;
  onNoteChange: (value: string) => void;
  provider: AdminProviderAccount;
}) {
  const { t } = useAppPreferences();
  const [reason, setReason] = useState<AdminRejectReason | "">("");
  const [missing, setMissing] = useState(false);
  const noteId = useId();
  const reasonId = useId();
  const decisions = providerDecisions(provider.approval_status, isAdmin);
  const needsReason = decisions.some((status) => status === "rejected" || status === "suspended");
  if (decisions.length === 0) return null;

  const decide = (status: ProviderApprovalStatus) => {
    if ((status === "rejected" || status === "suspended") && (!note.trim() || !reason)) {
      setMissing(true);
      return;
    }
    setMissing(false);
    onDecide({ status, note, reason });
  };

  return (
    <div className="console-decision">
      <label className="ops-field" htmlFor={noteId}>
        <span className="ops-label">{t("Note to the provider")}</span>
        <textarea
          aria-label={t("Review note for {name}", { name: provider.business_name })}
          className="ops-textarea"
          id={noteId}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder={t("Explain the decision. The owner sees this.")}
          rows={2}
          value={note}
        />
      </label>
      {needsReason ? (
        <label className="ops-field" htmlFor={reasonId}>
          <span className="ops-label">{t("Reason (needed to reject or suspend)")}</span>
          <select
            aria-label={t("Decision reason for {name}", { name: provider.business_name })}
            className="ops-select"
            id={reasonId}
            onChange={(event) => setReason(event.target.value as AdminRejectReason | "")}
            value={reason}
          >
            <option value="">{t("Select a reason")}</option>
            {REJECT_REASONS.map((item) => (
              <option key={item.value} value={item.value}>{t(item.label)}</option>
            ))}
          </select>
        </label>
      ) : null}
      {missing ? <p className="console-error" role="alert">{t("Add a note and pick a reason before rejecting or suspending.")}</p> : null}
      <div className="console-button-row">
        {decisions.map((status) => (
          <button
            className="ops-button"
            data-size="sm"
            data-variant={status === "approved" ? "primary" : status === "suspended" ? "warning" : "danger"}
            disabled={busy}
            key={status}
            onClick={() => decide(status)}
            type="button"
          >
            {status === "approved"
              ? provider.approval_status === "suspended"
                ? t("Restore access")
                : t("Approve")
              : status === "rejected"
                ? t("Reject")
                : t("Suspend")}
          </button>
        ))}
      </div>
      {!isAdmin && provider.approval_status !== "pending" ? null : !isAdmin ? (
        <p className="console-muted">{t("Only admins can reject or suspend a provider.")}</p>
      ) : null}
    </div>
  );
}
